import { findFencedBlock } from './factory-questions';
import { TASK_CHAT_MAX, TASK_DESCRIPTION_MAX, TASK_KINDS, TASK_SIZES, TASK_TITLE_MAX, type TaskKind, type TaskSize } from './types/tasks';

/**
 * T-51: task changes a chat agent proposes. It never writes to a board itself: it ends its message with
 *
 * ```factory-changes
 * { "changes": [
 *   { "id": "new-retry", "action": "create", "project": "software-factory", "title": "…", "description": "…", "column": "Backlog", "ask_feedback": true },
 *   { "id": "retry-part", "action": "create", "project": "software-factory", "parent": "new-retry", "title": "…" },
 *   { "id": "rename", "action": "edit", "task": "SF-T-12", "title": "…" },
 *   { "id": "under", "action": "edit", "task": "SF-T-13", "parent": "new-retry" },
 *   { "id": "note", "action": "comment", "task": "SF-T-12", "body": "…" },
 *   { "id": "ready", "action": "move", "task": "SF-T-12", "column": "Ready" },
 *   { "id": "build", "action": "assign_ai", "task": "SF-T-12" },
 *   { "id": "order", "action": "order", "task": "SF-T-12", "subtasks": [{ "task": "SF-T-14", "after": ["SF-T-13"], "chain_break": true }], "order": ["SF-T-13", "SF-T-14"] }
 * ] }
 * ```
 *
 * The chat shows them as a card, a tickbox and editable fields each; the person applies the ones they
 * tick, as themselves (POST /api/chat/sessions/:id/changes). What was applied and what failed is a note in
 * the chat, which goes back to the agent with its next turn.
 */

export const FACTORY_CHANGES_LANG = 'factory-changes';
export const FACTORY_CHANGES_MAX = 20;

export const FACTORY_CHANGE_ACTIONS = ['create', 'edit', 'comment', 'move', 'assign_ai', 'order'] as const;
export type FactoryChangeAction = (typeof FACTORY_CHANGE_ACTIONS)[number];

export const FACTORY_CHANGE_LABELS: Record<FactoryChangeAction, string> = {
  create: 'New task',
  edit: 'Edit',
  comment: 'Comment',
  move: 'Move',
  assign_ai: 'Hand to the AI builder',
  order: 'Sub-task order',
};

/** SF-T-271: most sub-tasks one `order` change names. */
export const FACTORY_ORDER_SUBTASKS_MAX = 60;

interface ChangeBase {
  id: string;
  /** Why the agent proposes it, shown under the change. */
  why?: string;
}

export interface FactoryCreateChange extends ChangeBase {
  action: 'create';
  /** The project's slug or prefix; '' with a `parent`: the parent's project. */
  project: string;
  /**
   * SF-T-241: makes it a sub-task of an existing task ("SF-T-240", in the same project) or of another
   * create in the same block (its id), which is applied first.
   */
  parent?: string;
  title: string;
  description: string;
  /** A column's name; null: the project's Backlog (or its first column). */
  column: string | null;
  /** Also ask the AI builder for feedback on the new task (an @AI builder comment). */
  ask_feedback: boolean;
}

export interface FactoryEditChange extends ChangeBase {
  action: 'edit';
  /** "SF-T-12" */
  task: string;
  title?: string;
  description?: string;
  size?: TaskSize;
  kind?: TaskKind;
  /** SF-T-241: puts it under another task, as for a create's `parent` (a ref, or a create's id here). */
  parent?: string;
}

export interface FactoryCommentChange extends ChangeBase {
  action: 'comment';
  task: string;
  body: string;
}

export interface FactoryMoveChange extends ChangeBase {
  action: 'move';
  task: string;
  column: string;
}

export interface FactoryAssignAiChange extends ChangeBase {
  action: 'assign_ai';
  task: string;
}

/**
 * SF-T-271: what a parent's sub-tasks wait on, and where its one-at-a-time chain stops for a person (from `sf-agent
 * tasks graph suggest`, or the agent's own reading). Applied in one go; only sub-tasks still to build change.
 */
export interface FactoryOrderChange extends ChangeBase {
  action: 'order';
  /** The parent: "SF-T-12". */
  task: string;
  /** Each sub-task it changes: the ones it waits on (refs; none: the one before it in the list) and, given, its break. */
  subtasks: Array<{ task: string; after: string[]; chain_break?: boolean }>;
  /** The list's new order (every sub-task's ref), when it moves them. */
  order?: string[];
}

export type FactoryChange = FactoryCreateChange | FactoryEditChange | FactoryCommentChange | FactoryMoveChange | FactoryAssignAiChange | FactoryOrderChange;

export interface FactoryChanges {
  changes: FactoryChange[];
}

/** One change's outcome, as the chat's note keeps it. */
export interface FactoryChangeResult {
  id: string;
  ok: boolean;
  /** What happened, in a line: "Created SF-T-60", or why it failed. */
  detail: string;
  /** The task it made or changed, to link. */
  ref?: string | null;
  /** SF-T-241: the task it was made a sub-task of, or put under (its ref). */
  parent?: string;
  /**
   * It failed in a way that leaves it unclear whether the write landed (a server or database error, not a
   * refusal): it stays recorded as applied, so it's never written twice, and the person checks the task.
   */
  uncertain?: boolean;
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const line = (v: unknown, max: number): string => text(v, max).replace(/\s*\n\s*/g, ' ');

/** A task ref with its project's prefix ("sf-t-12" → "SF-T-12"); '' for anything else. */
export function fullTaskRef(value: unknown): string {
  const m = /^\s*([a-z]{2})-t-?(\d{1,9})\s*$/i.exec(typeof value === 'string' ? value : '');
  return m ? `${m[1].toUpperCase()}-T-${Number(m[2])}` : '';
}

/**
 * Why a person's edited change is too long to apply as it is (the agent's own block is cut to fit
 * instead), or null. Their text is never cut short without saying so.
 */
export function changeTooLong(item: unknown): string | null {
  const c = (item ?? {}) as Record<string, unknown>;
  const over = (v: unknown, max: number) => typeof v === 'string' && v.trim().length > max;
  if (over(c.title, TASK_TITLE_MAX)) return `The title is longer than ${TASK_TITLE_MAX} characters`;
  if (over(c.description, TASK_DESCRIPTION_MAX)) return `The description is longer than ${TASK_DESCRIPTION_MAX} characters`;
  if (over(c.body, TASK_CHAT_MAX)) return `The comment is longer than ${TASK_CHAT_MAX} characters`;
  return null;
}

/** One change, checked and trimmed; null when it isn't usable. Also checks a person's edited copy. */
export function parseFactoryChange(item: unknown, fallbackId: string): FactoryChange | null {
  const c = (item ?? {}) as Record<string, unknown>;
  const id = line(c.id, 60) || fallbackId;
  const why = text(c.why, 1000);
  const base = { id, ...(why ? { why } : {}) };
  const action = typeof c.action === 'string' ? c.action.trim().toLowerCase().replace(/[\s-]+/g, '_') : '';
  if (action === 'create') {
    const title = line(c.title, TASK_TITLE_MAX);
    const project = line(c.project, 80);
    // A task ref in full ("sf-t-240" → "SF-T-240"), else another create's id.
    const parent = fullTaskRef(c.parent) || line(c.parent, 60);
    if (!title || (!project && !parent)) return null;
    return {
      ...base,
      action,
      project,
      ...(parent ? { parent } : {}),
      title,
      description: text(c.description, TASK_DESCRIPTION_MAX),
      column: line(c.column, 80) || null,
      ask_feedback: c.ask_feedback === true,
    };
  }
  const task = fullTaskRef(c.task);
  if (!task) return null;
  if (action === 'edit') {
    const out: FactoryEditChange = { ...base, action, task };
    const title = line(c.title, TASK_TITLE_MAX);
    if (title) out.title = title;
    // Given (even empty, a person clearing it): changed; left out: kept.
    if (typeof c.description === 'string') out.description = text(c.description, TASK_DESCRIPTION_MAX);
    const size = typeof c.size === 'string' ? c.size.trim().toUpperCase() : '';
    if ((TASK_SIZES as readonly string[]).includes(size)) out.size = size as TaskSize;
    const kind = typeof c.kind === 'string' ? c.kind.trim().toLowerCase() : '';
    if ((TASK_KINDS as readonly string[]).includes(kind)) out.kind = kind as TaskKind;
    // Given (even empty, a person still picking one): it goes under a parent; left out: it stays where it is.
    if (typeof c.parent === 'string') out.parent = fullTaskRef(c.parent) || line(c.parent, 60);
    return out.title || out.description !== undefined || out.size || out.kind || out.parent !== undefined ? out : null;
  }
  if (action === 'comment') {
    const body = text(c.body, TASK_CHAT_MAX);
    return body ? { ...base, action, task, body } : null;
  }
  if (action === 'move') {
    const column = line(c.column, 80);
    return column ? { ...base, action, task, column } : null;
  }
  if (action === 'assign_ai' || action === 'assign') return { ...base, action: 'assign_ai', task };
  if (action === 'order') {
    const refs = (v: unknown) => (Array.isArray(v) ? [...new Set(v.map(fullTaskRef).filter(Boolean))] : []);
    const seen = new Set<string>();
    const subtasks: FactoryOrderChange['subtasks'] = [];
    for (const item of Array.isArray(c.subtasks) ? c.subtasks.slice(0, FACTORY_ORDER_SUBTASKS_MAX) : []) {
      const e = (item ?? {}) as Record<string, unknown>;
      const ref = fullTaskRef(e.task);
      if (!ref || ref === task || seen.has(ref)) continue;
      seen.add(ref);
      subtasks.push({ task: ref, after: refs(e.after).filter((r) => r !== ref), ...(typeof e.chain_break === 'boolean' ? { chain_break: e.chain_break } : {}) });
    }
    const order = refs(c.order).slice(0, FACTORY_ORDER_SUBTASKS_MAX);
    return subtasks.length || order.length ? { ...base, action, task, subtasks, ...(order.length ? { order } : {}) } : null;
  }
  return null;
}

/** The block's JSON, checked and trimmed; null when no change in it is usable. Ids are made unique. */
export function parseFactoryChangesJson(raw: string): FactoryChanges | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = (Array.isArray(data) ? { changes: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.changes)) return null;
  const changes: FactoryChange[] = [];
  const ids = new Set<string>();
  /** Ids given to more than one change: a parent naming one can't say which it means. */
  const repeated = new Set<string>();
  for (const [i, item] of obj.changes.slice(0, FACTORY_CHANGES_MAX).entries()) {
    const change = parseFactoryChange(item, `c${i + 1}`);
    if (!change) continue;
    // Until it's unique: a made-up id can itself be one the agent gave.
    if (ids.has(change.id)) {
      const base = change.id;
      repeated.add(base);
      for (let n = i + 1; ids.has(change.id); n++) change.id = `${base}-${n}`;
    }
    ids.add(change.id);
    changes.push(change);
  }
  // SF-T-241: a parent naming a repeated id matches none of them (the first is renamed too), so applying it
  // says so rather than guessing; the person picks the parent on the card.
  const ambiguous = new Set(changes.flatMap((c) => ((c.action === 'create' || c.action === 'edit') && c.parent && repeated.has(c.parent) ? [c.parent] : [])));
  for (const id of ambiguous) {
    const first = changes.find((c) => c.id === id);
    if (!first) continue;
    for (let n = 1; ids.has(first.id); n++) first.id = `${id}-${n}`;
    ids.add(first.id);
  }
  for (const change of changes) {
    if (change.action !== 'create' || !change.parent) continue;
    if (change.parent === change.id) delete change.parent;
    if (change.parent && ambiguous.has(change.parent)) continue;
    // A sub-task of a new task goes in that task's project when it names none.
    const parent = parentCreate(changes, change);
    if (parent && !change.project) change.project = parent.project;
    else if (!change.project && change.parent && !fullTaskRef(change.parent)) delete change.parent;
  }
  // With no project, a parent that isn't a task or a create here leaves nowhere to put it.
  const usable = changes.filter((c) => c.action !== 'create' || c.project || c.parent);
  return usable.length ? { changes: usable } : null;
}

/** The first factory-changes block in a message, with where it sits; null when there's none (or it's broken). */
export function findFactoryChanges(message: string): { changes: FactoryChanges; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_CHANGES_LANG);
  const changes = block ? parseFactoryChangesJson(block.body) : null;
  if (!block || !changes) return null;
  return { changes, start: block.start, end: block.end };
}

/** The create in the same block a create's or edit's `parent` names (by its id), if it names one; a task ref is never one. */
export function parentCreate(changes: readonly FactoryChange[], change: { id: string; parent?: string }): FactoryCreateChange | undefined {
  if (!change.parent || fullTaskRef(change.parent)) return undefined;
  return changes.find((c): c is FactoryCreateChange => c.action === 'create' && c.id === change.parent && c.id !== change.id);
}

/** A change in a line, for the note and the agent: "Move SF-T-12 to Ready". */
export function describeChange(c: FactoryChange): string {
  switch (c.action) {
    case 'create':
      return `Create "${c.title}" in ${c.project || "its parent's project"}${c.column ? ` (${c.column})` : ''}${c.parent ? `, as a sub-task of ${c.parent}` : ''}${c.ask_feedback ? ', asking the AI builder for feedback' : ''}`;
    case 'edit': {
      const fields = [c.title ? 'title' : null, c.description !== undefined ? 'description' : null, c.size ? 'size' : null, c.kind ? 'kind' : null].filter(Boolean).join(', ');
      if (c.parent === undefined) return `Edit ${c.task}'s ${fields}`;
      const under = `under ${c.parent || 'a parent'}`;
      return fields ? `Edit ${c.task}'s ${fields}, and put it ${under}` : `Put ${c.task} ${under}`;
    }
    case 'comment':
      return `Comment on ${c.task}`;
    case 'move':
      return `Move ${c.task} to ${c.column}`;
    case 'assign_ai':
      return `Hand ${c.task} to the AI builder`;
    case 'order':
      return `Set the order of ${c.task}'s sub-tasks`;
  }
}

/** SF-T-271: one sub-task's line in an `order` change: "SF-T-14 after SF-T-13; stops here for a person". */
export function orderEntryText(e: FactoryOrderChange['subtasks'][number]): string {
  const parts = [e.after.length ? `after ${e.after.join(', ')}` : 'after the one before it in the list'];
  if (e.chain_break !== undefined) parts.push(e.chain_break ? 'stops here for a person' : 'no stop here');
  return `${e.task} ${parts.join('; ')}`;
}

/** The note the chat shows (and the agent reads next turn) for applied changes. */
export function formatChangeResults(results: FactoryChangeResult[]): string {
  const ok = results.filter((r) => r.ok).length;
  const head = ok === results.length ? `Applied ${ok === 1 ? 'the change' : `${ok} changes`}:` : `Applied ${ok} of ${results.length} changes:`;
  return [head, ...results.map((r) => `- ${r.ok ? '' : 'Failed: '}${r.detail}`)].join('\n');
}

/** Whether a change has what applying it needs (a create's title and project, a comment's body, …). */
export function changeComplete(c: FactoryChange): boolean {
  switch (c.action) {
    case 'create':
      return Boolean(c.title.trim() && (c.project.trim() || c.parent?.trim()));
    case 'edit':
      return (c.title === undefined || Boolean(c.title.trim())) && (c.parent === undefined || Boolean(c.parent.trim()));
    case 'comment':
      return Boolean(c.body.trim());
    case 'move':
      return Boolean(c.column.trim());
    case 'assign_ai':
      return true;
    case 'order':
      return c.subtasks.length > 0 || Boolean(c.order?.length);
  }
}
